import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import { FlowDocAction } from "./DocAction";

/*
 * Accounts-flow status for documents of the existing customer app (order,
 * loading advice, invoice, purchase) and the button that opens the receipt or
 * payment screen for one. Cells on a page are batched into one request per kind
 * and type, and refreshed every time a list is opened (no stale buttons).
 */

const cache = new Map();
const listeners = new Map();
const queue = {};
let timer = null;

const FETCH = {
  source: (type, refs) => accountsFlowApi.sourceStatus(type, refs),
  doc: (type, refs) => accountsFlowApi.docStatus(type, refs),
};
const keyOf = (kind, type, ref) => `${kind}|${type}|${ref}`;

function notify(key) {
  (listeners.get(key) || new Set()).forEach((listener) => listener(cache.get(key)));
}

async function flush() {
  timer = null;
  const work = Object.entries(queue).map(([bucket, refs]) => [bucket, [...refs]]);
  Object.keys(queue).forEach((bucket) => delete queue[bucket]);
  await Promise.all(work.map(async ([bucket, refs]) => {
    const [kind, type] = bucket.split("|");
    for (let start = 0; start < refs.length; start += 100) {
      const chunk = refs.slice(start, start + 100);
      let result = {};
      try {
        result = (await FETCH[kind](type, chunk))?.data || {};
      } catch (err) {
        result = {};
      }
      chunk.forEach((ref) => {
        const key = keyOf(kind, type, ref);
        cache.set(key, result[ref] || { empty: true });
        notify(key);
      });
    }
  }));
}

function enqueue(kind, type, ref) {
  const bucket = `${kind}|${type}`;
  (queue[bucket] || (queue[bucket] = new Set())).add(ref);
  if (!timer) timer = setTimeout(flush, 30);
}

function useFlowStatus(kind, type, ref) {
  const key = keyOf(kind, type, ref);
  const [status, setStatus] = useState(undefined);
  useEffect(() => {
    if (!ref) return undefined;
    const set = listeners.get(key) || new Set();
    set.add(setStatus);
    listeners.set(key, set);
    cache.delete(key); // always fresh when a list is opened
    setStatus(undefined);
    enqueue(kind, type, ref);
    return () => set.delete(setStatus);
  }, [key, kind, type, ref]);
  return status;
}

export const useSourceStatus = (type, ref) => useFlowStatus("source", type, ref);

function money(value) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(Number(value) || 0);
}

function moneyQuery(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number.toFixed(2) : "";
}

/* Receipt or payment screen pre-filled from a customer-app document. */
export function sourceEntryLink(voucherType, source) {
  const params = new URLSearchParams();
  Object.entries({
    sourceType: source.type,
    invoiceNr: source.invoiceNr,
    loadingAdviceNr: source.loadingAdviceNr,
    orderNo: source.orderNo,
    documentNo: source.documentNo,
    customerCode: source.customerCode,
    partyName: source.partyName,
    referenceNo: source.invoiceNr || source.documentNo || source.loadingAdviceNr || source.orderNo,
    amount: moneyQuery(source.amount),
  }).forEach(([key, value]) => { if (value) params.set(key, value); });
  const path = voucherType === "PAYMENT" ? AccountsFlowConstants.PAYMENT_ENTRY : AccountsFlowConstants.RECEIPT_ENTRY;
  return `${path}?${params.toString()}`;
}

/* Value of loaded items including GST: quantity confirmed for loading x unit price x (1 + GST%). */
export function loadedValue(items) {
  const total = (items || []).reduce((sum, item) => {
    const qty = Number(item?.orderQtyLoadConfirm ?? item?.loadQty ?? item?.orderQty);
    const price = Number(item?.unitPrice);
    const gst = Number(item?.gst ?? (Number(item?.cgst || 0) + Number(item?.sgst || 0)));
    if (!Number.isFinite(qty) || !Number.isFinite(price)) return sum;
    return sum + qty * price * (1 + (Number.isFinite(gst) ? gst : 0) / 100);
  }, 0);
  return Math.round(total * 100) / 100;
}

const STATUS_TONE = {
  APPROVED: "bg-table-success-status",
  PENDING: "bg-table-warning-status",
  REJECTED: "bg-table-danger-status",
  CANCELLED: "bg-table-danger-status",
  Paid: "bg-table-success-status",
  "Part Paid": "bg-table-info-status",
  "Receipt Pending": "bg-table-warning-status",
  "Receipt Rejected": "bg-table-danger-status",
  Cancelled: "bg-table-danger-status",
};

const STATUS_ICON = {
  Paid: "bi-check-circle",
  "Part Paid": "bi-pie-chart",
  "Receipt Pending": "bi-hourglass-split",
  "Receipt Rejected": "bi-x-circle",
  Cancelled: "bi-slash-circle",
  Issued: "bi-truck",
  Invoiced: "bi-file-earmark-check",
  "Invoice Created": "bi-file-earmark-plus",
  APPROVED: "bi-check-circle",
  PENDING: "bi-hourglass-split",
  REJECTED: "bi-x-circle",
  CANCELLED: "bi-slash-circle",
};

const STATUS_CLASS = {
  Paid: "is-ok",
  "Part Paid": "is-info",
  "Receipt Pending": "is-warn",
  "Receipt Rejected": "is-bad",
  Cancelled: "is-bad",
  Issued: "is-info",
  Invoiced: "is-ok",
  APPROVED: "is-ok",
  PENDING: "is-warn",
  REJECTED: "is-bad",
  CANCELLED: "is-bad",
};

function Chip({ text }) {
  return <span className={`bg-table-status ${STATUS_TONE[text] || "bg-table-new-status"}`}>{text}</span>;
}

function StatusPill({ text }) {
  return (
    <span className={`flow-doc-status fw-bold ${STATUS_CLASS[text] || "is-info"}`}>
      <i className={`bi ${STATUS_ICON[text] || "bi-info-circle"}`} aria-hidden="true" />
      {text}
    </span>
  );
}

/* Lifecycle status of an order, loading advice or invoice (Created, Issued, Invoiced, Receipt Pending, Part Paid, Paid ...). */
export function DocStatusCell({ type, refNo }) {
  const status = useFlowStatus("doc", type, refNo);
  if (!refNo) return <span>--</span>;
  if (!status) return <span className="text-muted">…</span>;
  if (status.empty) return <span>--</span>;
  const history = (status.history || []).map((h) => `${h.status}${h.note ? ` (${h.note})` : ""}`).join("\n");
  return <span title={history}><Chip text={status.status} /></span>;
}

/* Receipt / payment status for a document (used on the Purchase list). */
export function SourceStatusCell({ type, refNo, emptyText = "--" }) {
  const status = useSourceStatus(type, refNo);
  if (!refNo) return <span>{emptyText}</span>;
  if (!status) return <span className="text-muted">…</span>;
  if (status.empty || status.none) return <span>{emptyText}</span>;
  return (
    <div title={(status.entries || []).map((row) => `${row.no} ${row.status} ${money(row.amount)}`).join("\n")}>
      <Chip text={status.lastStatus} />
      {status.approvedAmount > 0 && <p className="m-0 p-0 small fw-bold text-success">Approved {money(status.approvedAmount)}</p>}
      {status.pendingAmount > 0 && <p className="m-0 p-0 small fw-bold text-warning">Pending {money(status.pendingAmount)}</p>}
    </div>
  );
}

/*
 * Receipt cell for a customer-app invoice: lifecycle status, approved / pending
 * amounts, and the Receipt button only while part of the invoice is still open
 * (pre-filled with that balance).
 */
export function InvoiceReceiptCell({ source, cancelled }) {
  const navigate = useNavigate();
  const doc = useFlowStatus("doc", "INVOICE", source.invoiceNr);
  const receipts = useSourceStatus("INVOICE", source.invoiceNr);
  if (!source.invoiceNr) return <span>--</span>;
  if (!doc || !receipts) return <span className="text-muted">…</span>;
  const position = receipts.invoice || null;
  const remaining = position ? position.remaining : 0;
  const started = position && (position.approved > 0 || position.pending > 0);
  const history = (receipts.entries || []).map((row) => `${row.no} ${row.status} ${money(row.amount)}`).join("\n");
  return (
    <div className="flow-doc-actions" title={history}>
      {doc.empty ? <span>--</span> : <StatusPill text={doc.status} />}
      {position && position.approved > 0 && <p className="flow-doc-money is-in fw-bold">Received {money(position.approved)}</p>}
      {position && position.pending > 0 && <p className="flow-doc-money is-wait fw-bold">Waiting {money(position.pending)}</p>}
      {!cancelled && remaining > 0 && (
        <FlowDocAction
          icon="bi-cash-coin"
          label="Receipt"
          tone={started ? "warn" : ""}
          title={started ? `Record a receipt for the open balance ${money(remaining)}` : "Record a receipt for this invoice"}
          onClick={() => navigate(sourceEntryLink("RECEIPT", { ...source, amount: remaining }))}
        />
      )}
    </div>
  );
}

export function SourceEntryButton({ voucherType = "RECEIPT", source, label }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="btnTable" onClick={() => navigate(sourceEntryLink(voucherType, source))}>
      {label || (voucherType === "PAYMENT" ? "Payment" : "Receipt")}
    </button>
  );
}
